import React, { useEffect, useState } from "react";
import api from "../../../api";
import { IMAGE_URL } from "../../../path";
import { RecipeCategory } from "../../../Recipe";

interface RecipeCategoryModalProps {
    category: RecipeCategory | null;
    onClose: () => void;
    onSaved: () => void;
}

const RecipeCategoryModal: React.FC<RecipeCategoryModalProps> = ({
                                                                     category,
                                                                     onClose,
                                                                     onSaved,
                                                                 }) => {
    const [name, setName] = useState("");
    const [icon, setIcon] = useState<File | null>(null);
    const [iconPreview, setIconPreview] = useState<string | null>(null);
    const [active, setActive] = useState(true);
    const [displayOrder, setDisplayOrder] = useState(0);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        if (category) {
            setName(category.name || "");
            setActive(category.active ?? true);
            setDisplayOrder(category.display_order ?? 0);
            setIcon(null);

            if (category.icon) {
                setIconPreview(
                    `${IMAGE_URL}/${category.icon}`
                );
            } else {
                setIconPreview(null);
            }
        } else {
            setName("");
            setIcon(null);
            setIconPreview(null);
            setActive(true);
            setDisplayOrder(0);
        }

        setError("");
        setSuccess("");
    }, [category]);

    /*
     * =========================================================
     * ICON
     * =========================================================
     */

    const handleIconChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0] || null;

        if (!file) {
            return;
        }

        setError("");

        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "image/gif",
            "image/webp",
            "image/svg+xml",
        ];

        const allowedExtensions = [
            ".jpg",
            ".jpeg",
            ".png",
            ".gif",
            ".webp",
            ".svg",
        ];

        const extension = `.${file.name
            .split(".")
            .pop()
            ?.toLowerCase()}`;

        if (
            !allowedTypes.includes(file.type) &&
            !allowedExtensions.includes(extension)
        ) {
            setError(
                "Please select a JPG, PNG, GIF, WEBP, or SVG image."
            );

            e.target.value = "";
            return;
        }

        setIcon(file);

        const previewUrl = URL.createObjectURL(file);

        setIconPreview(previewUrl);
    };

    /*
     * =========================================================
     * SAVE
     * =========================================================
     */

    const handleSave = async () => {
        try {
            setSaving(true);
            setError("");
            setSuccess("");

            if (!name.trim()) {
                setError("Please enter a category name.");
                return;
            }

            const formData = new FormData();

            formData.append(
                "name",
                name.trim()
            );

            formData.append(
                "active",
                active ? "1" : "0"
            );

            formData.append(
                "display_order",
                String(displayOrder)
            );

            if (icon) {
                formData.append(
                    "icon",
                    icon
                );
            }

            if (category) {
                formData.append(
                    "_method",
                    "PUT"
                );

                await api.post(
                    `/admin/recipe-categories/${category.id}`,
                    formData,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );

                setSuccess(
                    "Recipe category updated successfully."
                );
            } else {
                await api.post(
                    "/admin/recipe-categories",
                    formData,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );

                setSuccess(
                    "Recipe category added successfully."
                );
            }

            onSaved();
        } catch (err: any) {
            console.error(
                "Failed to save recipe category:",
                err
            );

            const validationErrors =
                err?.response?.data?.errors;

            if (validationErrors) {
                const firstError =
                    Object.values(
                        validationErrors
                    )[0];

                if (
                    Array.isArray(firstError) &&
                    firstError.length > 0
                ) {
                    setError(
                        firstError[0] as string
                    );
                } else {
                    setError(
                        "Please check the form and try again."
                    );
                }
            } else {
                setError(
                    err?.response?.data?.message ||
                    "Unable to save recipe category. Please try again."
                );
            }
        } finally {
            setSaving(false);
        }
    };

    /*
     * =========================================================
     * CLOSE
     * =========================================================
     */

    const handleClose = () => {
        if (iconPreview && icon) {
            URL.revokeObjectURL(iconPreview);
        }

        onClose();
    };

    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal">

                <div className="admin-modal-header">
                    <h2>
                        {category
                            ? "Edit Recipe Category"
                            : "Add Recipe Category"}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={handleClose}
                        disabled={saving}
                    >
                        ×
                    </button>
                </div>

                <div className="admin-modal-content">

                    {error && (
                        <div className="form-error">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="form-success">
                            {success}
                        </div>
                    )}

                    {/* =====================================================
                        NAME
                    ====================================================== */}

                    <div className="form-group">
                        <label htmlFor="recipe-category-name">
                            Category Name
                        </label>

                        <input
                            id="recipe-category-name"
                            type="text"
                            value={name}
                            onChange={(e) =>
                                setName(e.target.value)
                            }
                            placeholder="Category name"
                            disabled={saving}
                        />
                    </div>

                    {/* =====================================================
                        ICON
                    ====================================================== */}

                    <div className="form-group">
                        <label htmlFor="recipe-category-icon">
                            Icon
                        </label>

                        <input
                            id="recipe-category-icon"
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                            onChange={handleIconChange}
                            disabled={saving}
                        />

                        <small>
                            JPG, PNG, GIF, WEBP, or SVG.
                        </small>
                    </div>

                    {/* =====================================================
                        ICON PREVIEW
                    ====================================================== */}

                    {iconPreview && (
                        <div className="form-group">
                            <label>
                                Icon Preview
                            </label>

                            <img
                                src={iconPreview}
                                alt="Recipe category icon preview"
                                className="admin-svg-preview-large"
                            />
                        </div>
                    )}

                    {/* =====================================================
                        ACTIVE
                    ====================================================== */}

                    <div className="form-group">
                        <label className="admin-checkbox-label">
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) =>
                                    setActive(
                                        e.target.checked
                                    )
                                }
                                disabled={saving}
                            />

                            <span>
                                Active
                            </span>
                        </label>
                    </div>

                    {/* =====================================================
                        DISPLAY ORDER
                    ====================================================== */}

                    <div className="form-group">
                        <label htmlFor="recipe-category-display-order">
                            Display Order
                        </label>

                        <input
                            id="recipe-category-display-order"
                            type="number"
                            min="0"
                            value={displayOrder}
                            onChange={(e) =>
                                setDisplayOrder(
                                    Number(e.target.value)
                                )
                            }
                            disabled={saving}
                        />
                    </div>

                    {/* =====================================================
                        ACTIONS
                    ====================================================== */}

                    <div className="admin-modal-actions">
                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={handleClose}
                            disabled={saving}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="admin-primary-button"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving
                                ? "Saving..."
                                : category
                                    ? "Save Changes"
                                    : "Add Category"}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default RecipeCategoryModal;
